// src/components/adhoc/AdhocProfileStage.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Kiosk profile form — visually polished for all display sizes.
// Refined for high-accuracy layout on tablets, laptops, and large kiosk screens.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { ShieldPlus, User } from 'lucide-react';
import AdhocFlowLayout from './AdhocFlowLayout';
import { SDK_AGE_MAX, SDK_AGE_MIN } from '../../utils/userProfile';
import { getPrivacyPolicyUrl } from '../../../../shared/privacyPolicy';
import './profile.css';

// Landing-aligned design tokens (flow shell)
const C = {
  INK: '#0F2A33',
  MUTED: '#4B6B72',
  MUTED_LIGHT: '#4B6B72',
  TEAL: '#12A594',
  TEAL_DARK: '#0B3B4F',
  TEAL_FOCUS: 'rgba(18, 165, 148, 0.14)',
  GREEN_DEEP: '#3F7A2A',
  PANEL: '#F6FAF9',
  PANEL_LINE: '#DCEAE7',
  LINE: '#DCEAE7',
  WHITE: '#ffffff',
  RED600: '#dc2626',
  RED300: '#fca5a5',
  RED50: '#fffafa',
};

const FONT_BODY = "'Inter', sans-serif";
const FONT_MONO = "'IBM Plex Mono', monospace";

// ── Styled Components ────────────────────────────────────────────────────────

const FormBlock = styled.div`
  margin-bottom: 24px;
`;

const SectionLabel = styled.p`
  font-family: ${FONT_MONO};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${C.TEAL};
  margin: 0 0 12px;
`;

const Input = styled.input<{ hasError?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  padding: 16px 14px;
  font-size: 16px;
  font-weight: 500;
  border: 1px solid ${p => p.hasError ? C.RED300 : C.PANEL_LINE};
  border-radius: 14px;
  outline: none;
  background: ${p => p.hasError ? C.RED50 : C.PANEL};
  color: ${C.INK};
  font-family: ${FONT_BODY};
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;

  &:focus {
    border-color: ${p => p.hasError ? C.RED300 : C.TEAL};
    box-shadow: ${p => p.hasError ? 'none' : `0 0 0 4px ${C.TEAL_FOCUS}`};
    background: ${C.WHITE};
  }
`;

const ErrorText = styled.p`
  font-size: 13px;
  color: ${C.RED600};
  margin: 8px 0 0;
  font-weight: 500;
`;

const HintText = styled.p`
  font-family: ${FONT_MONO};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${C.MUTED_LIGHT};
  margin: 8px 0 0;
`;

// ── Types ────────────────────────────────────────────────────────────────────
export type MallSex      = 'male' | 'female' | 'unspecified';
export type MallSmoking  = 'non_smoker' | 'smoker' | 'unspecified';
export type UnitSystem    = 'metric' | 'imperial';

export interface AdhocProfileData {
  guest_name?:    string;
  email?:         string;
  sex?:           MallSex;
  age?:           number;
  height_cm?:     number;
  weight_kg?:     number;
  smoking_status?: MallSmoking;
  diet_plan?:     boolean;
  chronic_illness?: boolean;
}

interface Props {
  sessionEmail?: string | null;
  ownerMode?: boolean;
  ownerName?: string | null;
  ownerEmail?: string | null;
  onContinue: (data: AdhocProfileData) => void;
  saving?: boolean;
  error?: string | null;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const lbsToKg = (lbs: number) => lbs * 0.453592;
const ftInToCm = (ft: number, inch: number) => (ft * 30.48) + (inch * 2.54);

// ── Sub-components ──────────────────────────────────────────────────────────

const SexCardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
`;

const SexOptionButton = styled.button<{ active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px 8px;
  min-height: 100px;
  border-radius: 18px;
  border: 1px solid ${p => p.active ? C.TEAL : C.PANEL_LINE};
  background: ${p => p.active ? C.WHITE : C.PANEL};
  color: ${p => p.active ? C.TEAL_DARK : C.INK};
  cursor: pointer;
  font-family: ${FONT_BODY};
  transition: all 0.2s ease;
  box-shadow: ${p => p.active ? '0 4px 14px -6px rgba(63, 156, 140, 0.25)' : 'none'};

  &:hover:not(:disabled) {
    border-color: ${C.TEAL};
    transform: translateY(-1px);
  }

  span {
    font-size: 13px;
    font-weight: 600;
    line-height: 1.2;
    text-align: center;
  }
`;

function SexCards({ value, onChange }: { value: MallSex | null; onChange: (v: MallSex) => void }) {
  const options: { value: MallSex; label: string; icon: any }[] = [
    {
      value: 'female', label: 'Female',
      icon: (active: boolean) => (
        <svg width={24} height={24} viewBox="0 0 24 24" fill="none"
          stroke={active ? C.TEAL_DARK : C.MUTED} strokeWidth="2.5" strokeLinecap="round">
          <circle cx="12" cy="9" r="5" />
          <path d="M12 14v7M9 18h6" />
        </svg>
      ),
    },
    {
      value: 'male', label: 'Male',
      icon: (active: boolean) => (
        <svg width={24} height={24} viewBox="0 0 24 24" fill="none"
          stroke={active ? C.TEAL_DARK : C.MUTED} strokeWidth="2.5" strokeLinecap="round">
          <circle cx="10" cy="14" r="5" />
          <path d="M15 9l5-5M20 4h-5M20 4v5" />
        </svg>
      ),
    },
    {
      value: 'unspecified', label: 'Other',
      icon: (active: boolean) => <User size={24} color={active ? C.TEAL_DARK : C.MUTED} strokeWidth={2.5} />,
    },
  ];

  return (
    <SexCardGrid>
      {options.map(({ value: opt, label, icon }) => {
        const active = value === opt;
        return (
          <SexOptionButton key={opt} type="button" active={active} onClick={() => onChange(opt)}>
            {icon(active)}
            <span>
              {label}
            </span>
          </SexOptionButton>
        );
      })}
    </SexCardGrid>
  );
}

const SuffixInputWrapper = styled.div<{ focused: boolean; hasError?: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  background: ${p => p.hasError ? C.RED50 : p.focused ? C.WHITE : C.PANEL};
  border-radius: 14px;
  border: 1px solid ${p => p.hasError ? C.RED300 : p.focused ? C.TEAL : C.PANEL_LINE};
  box-shadow: ${p => p.focused && !p.hasError ? `0 0 0 4px ${C.TEAL_FOCUS}` : 'none'};
  overflow: hidden;
  transition: all 0.2s ease;

  input {
    flex: 1;
    min-width: 0;
    padding: 16px 14px;
    font-size: 16px;
    font-weight: 500;
    border: none;
    outline: none;
    background: transparent;
    color: ${C.INK};
    font-family: ${FONT_BODY};
  }

  span {
    padding: 0 16px 0 4px;
    font-family: ${FONT_MONO};
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    color: ${C.MUTED_LIGHT};
    flex-shrink: 0;
  }
`;

function SuffixInput({
  value, onChange, onBlur, suffix, placeholder, hasError, type = 'number', inputMode,
}: {
  value: string; onChange: (v: string) => void; onBlur?: () => void;
  suffix: string; placeholder?: string; hasError?: boolean;
  type?: string; inputMode?: any;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <SuffixInputWrapper focused={focused} hasError={hasError}>
      <input
        type={type} inputMode={inputMode} value={value}
        onChange={e => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => { setFocused(false); onBlur?.(); }}
        placeholder={placeholder}
      />
      <span>{suffix}</span>
    </SuffixInputWrapper>
  );
}

const ToggleContainer = styled.div`
  display: inline-flex;
  background: ${C.PANEL};
  border: 1px solid ${C.PANEL_LINE};
  padding: 5px;
  border-radius: 12px;
  margin-bottom: 20px;
`;

const ToggleButton = styled.button<{ active: boolean }>`
  padding: 8px 20px;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 600;
  text-transform: capitalize;
  border: none;
  cursor: pointer;
  font-family: ${FONT_BODY};
  background: ${p => p.active ? C.WHITE : 'transparent'};
  color: ${p => p.active ? C.GREEN_DEEP : C.MUTED};
  box-shadow: ${p => p.active ? '0 2px 6px rgba(21, 38, 49, 0.08)' : 'none'};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    color: ${p => p.active ? C.GREEN_DEEP : C.INK};
  }
`;

function UnitToggle({ value, onChange }: { value: UnitSystem; onChange: (v: UnitSystem) => void }) {
  return (
    <ToggleContainer>
      {(['metric', 'imperial'] as UnitSystem[]).map(sys => (
        <ToggleButton key={sys} type="button" active={value === sys} onClick={() => onChange(sys)}>
          {sys}
        </ToggleButton>
      ))}
    </ToggleContainer>
  );
}

const YesNoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const YesNoButton = styled.button<{ active: boolean }>`
  padding: 16px 12px;
  border-radius: 14px;
  border: 1px solid ${p => p.active ? C.TEAL : C.PANEL_LINE};
  background: ${p => p.active ? C.WHITE : C.PANEL};
  color: ${p => p.active ? C.TEAL_DARK : C.INK};
  font-size: 15px;
  font-weight: 600;
  font-family: ${FONT_BODY};
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${p => p.active ? '0 4px 14px -6px rgba(63, 156, 140, 0.25)' : 'none'};

  &:hover:not(:disabled) {
    border-color: ${C.TEAL};
  }
`;

function YesNoToggle({
  value,
  onChange,
}: {
  value: boolean | null;
  onChange: (v: boolean) => void;
}) {
  return (
    <YesNoGrid>
      <YesNoButton type="button" active={value === true} onClick={() => onChange(true)}>
        Yes
      </YesNoButton>
      <YesNoButton type="button" active={value === false} onClick={() => onChange(false)}>
        No
      </YesNoButton>
    </YesNoGrid>
  );
}

const PrivacyNote = styled.p`
  font-size: 13px;
  line-height: 1.55;
  color: ${C.MUTED};
  margin: 8px 0 20px;
  text-align: center;
`;

// ── Main Component ────────────────────────────────────────────────────────────

export default function AdhocProfileStage({
  sessionEmail,
  ownerMode = false,
  ownerName,
  ownerEmail,
  onContinue,
  saving = false,
  error = null,
}: Props) {
  const [guestName,     setGuestName]     = useState(ownerName || '');
  const [email,         setEmail]         = useState(ownerEmail || sessionEmail || '');
  const [sex,           setSex]           = useState<MallSex | null>(null);
  const [age,           setAge]           = useState('');
  const [unitSystem,    setUnitSystem]    = useState<UnitSystem>('metric');
  
  const [heightCm,      setHeightCm]      = useState('');
  const [weightKg,      setWeightKg]      = useState('');
  const [heightFt,      setHeightFt]      = useState('');
  const [heightIn,      setHeightIn]      = useState('');
  const [weightLbs,     setWeightLbs]     = useState('');

  const [smokingAnswer, setSmokingAnswer] = useState<boolean | null>(null);
  const [dietPlan,        setDietPlan]        = useState<boolean | null>(null);
  const [chronicIllness,  setChronicIllness]  = useState<boolean | null>(null);
  const [submitted,     setSubmitted]     = useState(false);

  const ageError = useMemo(() => {
    if (!age.trim()) return null;
    const parsed = parseInt(age, 10);
    if (!Number.isFinite(parsed) || String(parsed) !== age.trim()) {
      return 'Enter a valid age';
    }
    if (parsed < SDK_AGE_MIN || parsed > SDK_AGE_MAX) {
      return `Age must be between ${SDK_AGE_MIN} and ${SDK_AGE_MAX} years`;
    }
    return null;
  }, [age]);

  const heightError = useMemo(() => {
    if (unitSystem === 'metric') {
      if (!heightCm.trim()) return null;
      const h = parseFloat(heightCm);
      if (isNaN(h) || h < 50 || h > 250) return 'Height must be between 50 and 250 cm';
    } else {
      if (!heightFt.trim() && !heightIn.trim()) return null;
      const ft = parseInt(heightFt) || 0;
      const inc = parseInt(heightIn) || 0;
      const totalCm = ftInToCm(ft, inc);
      if (totalCm < 50 || totalCm > 250) return 'Height must be between 1\'8" and 8\'2"';
    }
    return null;
  }, [unitSystem, heightCm, heightFt, heightIn]);

  const weightError = useMemo(() => {
    if (unitSystem === 'metric') {
      if (!weightKg.trim()) return null;
      const w = parseFloat(weightKg);
      if (isNaN(w) || w < 20 || w > 300) return 'Weight must be between 20 and 300 kg';
    } else {
      if (!weightLbs.trim()) return null;
      const lbs = parseFloat(weightLbs);
      const kg = lbsToKg(lbs);
      if (kg < 20 || kg > 300) return 'Weight must be between 44 and 660 lbs';
    }
    return null;
  }, [unitSystem, weightKg, weightLbs]);

  const nameError = useMemo(() => {
    if (!guestName.trim()) return 'Full name is required';
    return null;
  }, [guestName]);

  const emailError = useMemo(() => {
    const trimmed = email.trim();
    if (!trimmed) return 'Email is required';
    if (!EMAIL_RE.test(trimmed)) return 'Enter a valid email address';
    return null;
  }, [email]);

  const hasErrors = !!(nameError || emailError || ageError || heightError || weightError);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitted(true);
    if (hasErrors) return;

    const data: AdhocProfileData = {
      guest_name: guestName.trim(),
      email: email.trim(),
    };
    if (sex) data.sex = sex;
    if (age.trim() && !ageError) {
      data.age = parseInt(age, 10);
    }

    if (unitSystem === 'metric') {
      if (heightCm.trim()) data.height_cm = parseFloat(heightCm);
      if (weightKg.trim()) data.weight_kg = parseFloat(weightKg);
    } else {
      if (heightFt.trim() || heightIn.trim()) {
        data.height_cm = Math.round(ftInToCm(parseInt(heightFt) || 0, parseInt(heightIn) || 0));
      }
      if (weightLbs.trim()) {
        data.weight_kg = Math.round(lbsToKg(parseFloat(weightLbs)) * 10) / 10;
      }
    }
    if (smokingAnswer !== null) {
      data.smoking_status = smokingAnswer ? 'smoker' : 'non_smoker';
    }
    if (dietPlan !== null) data.diet_plan = dietPlan;
    if (chronicIllness !== null) data.chronic_illness = chronicIllness;

    onContinue(data);
  }

  return (
    <AdhocFlowLayout currentStep={2} stepLabel="Your details">
      <div className="adhoc-profile-form">
        <div className="profile-icon-wrap">
          <ShieldPlus size={28} strokeWidth={2.2} aria-hidden />
        </div>

        <h1 className="flow-title-legal profile-title">A quick health snapshot</h1>
        <p className="flow-legal-lede profile-desc">
          A few basics help us calibrate your report accurately. Takes less than a minute.
        </p>

        <form onSubmit={handleSubmit} noValidate className="profile-form">
          <div className="profile-fields">
            <FormBlock>
              <SectionLabel>Full Name</SectionLabel>
              <Input
                type="text" value={guestName} onChange={e => setGuestName(e.target.value)}
                placeholder="e.g. John Doe"
                hasError={!!(submitted && nameError)}
              />
              {submitted && nameError && <ErrorText>{nameError}</ErrorText>}
            </FormBlock>

            <FormBlock>
              <SectionLabel>Email address *</SectionLabel>
              {ownerMode ? (
                <>
                  <Input
                    type="email"
                    value={email}
                    readOnly
                    disabled
                    aria-readonly
                  />
                  <p className="profile-email-hint">
                    Reports will be sent to your signed-in email
                  </p>
                </>
              ) : (
                <>
                  <Input
                    type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    hasError={!!(submitted && emailError)}
                  />
                  {submitted && emailError ? (
                    <ErrorText>{emailError}</ErrorText>
                  ) : (
                    <p className="profile-email-hint">
                      We&apos;ll email your full PDF report here after the scan
                    </p>
                  )}
                </>
              )}
            </FormBlock>

            <FormBlock>
              <SectionLabel>Gender</SectionLabel>
              <SexCards value={sex} onChange={setSex} />
            </FormBlock>

            <FormBlock>
              <SectionLabel>Age</SectionLabel>
              <SuffixInput
                value={age}
                onChange={setAge}
                suffix="YEARS"
                placeholder="35"
                type="number"
                inputMode="numeric"
                hasError={!!(submitted && ageError)}
              />
              {submitted && ageError ? (
                <ErrorText>{ageError}</ErrorText>
              ) : (
                <HintText>Range: {SDK_AGE_MIN}–{SDK_AGE_MAX} years</HintText>
              )}
            </FormBlock>

            <FormBlock>
              <SectionLabel>Body measurements</SectionLabel>
              <UnitToggle value={unitSystem} onChange={setUnitSystem} />

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
                gap: 12,
              }}>
                {unitSystem === 'metric' ? (
                  <>
                    <div>
                      <SuffixInput
                        value={heightCm} onChange={setHeightCm}
                        suffix="CM" placeholder="172" type="number" inputMode="decimal"
                        hasError={!!(submitted && heightError)}
                      />
                      {submitted && heightError ? <ErrorText>{heightError}</ErrorText> : <HintText>Range: 50–250 CM</HintText>}
                    </div>
                    <div>
                      <SuffixInput
                        value={weightKg} onChange={setWeightKg}
                        suffix="KG" placeholder="70" type="number" inputMode="decimal"
                        hasError={!!(submitted && weightError)}
                      />
                      {submitted && weightError ? <ErrorText>{weightError}</ErrorText> : <HintText>Range: 20–300 KG</HintText>}
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <div style={{ display: 'flex', gap: 10 }}>
                        <SuffixInput
                          value={heightFt} onChange={setHeightFt}
                          suffix="FT" placeholder="5" type="number" inputMode="numeric"
                          hasError={!!(submitted && heightError)}
                        />
                        <SuffixInput
                          value={heightIn} onChange={setHeightIn}
                          suffix="IN" placeholder="8" type="number" inputMode="numeric"
                          hasError={!!(submitted && heightError)}
                        />
                      </div>
                      {submitted && heightError && <ErrorText>{heightError}</ErrorText>}
                    </div>
                    <div>
                      <SuffixInput
                        value={weightLbs} onChange={setWeightLbs}
                        suffix="LBS" placeholder="155" type="number" inputMode="decimal"
                        hasError={!!(submitted && weightError)}
                      />
                      {submitted && weightError ? <ErrorText>{weightError}</ErrorText> : <HintText>Range: 44–660 LBS</HintText>}
                    </div>
                  </>
                )}
              </div>
            </FormBlock>

            <FormBlock style={{ marginBottom: 16 }}>
              <SectionLabel>Do you smoke?</SectionLabel>
              <YesNoToggle value={smokingAnswer} onChange={setSmokingAnswer} />
            </FormBlock>

            <FormBlock style={{ marginBottom: 16 }}>
              <SectionLabel>Are you on a diet plan?</SectionLabel>
              <YesNoToggle value={dietPlan} onChange={setDietPlan} />
            </FormBlock>

            <FormBlock style={{ marginBottom: 8 }}>
              <SectionLabel>Any chronic illness?</SectionLabel>
              <YesNoToggle value={chronicIllness} onChange={setChronicIllness} />
            </FormBlock>

            <PrivacyNote>
              Your answers are used only to personalize your MyWellfie report. See our{' '}
              <a href={getPrivacyPolicyUrl()} target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>
              .
            </PrivacyNote>
          </div>

          <div className="flow-cta-row">
            <button type="submit" className="flow-primary" disabled={saving}>
              {saving ? 'Saving details…' : 'Continue'}
            </button>
            {error && <p className="flow-error" role="alert">{error}</p>}
          </div>
        </form>

        <footer className="profile-footer">
          <p>
            © {new Date().getFullYear()} MyWellfie.{' '}
            <a href={getPrivacyPolicyUrl()} target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
          </p>
        </footer>
      </div>
    </AdhocFlowLayout>
  );
}
